// Pure helpers that turn one AuditLogEntry (and, when available, the entry
// immediately before it in time) into a short, human-readable list of field
// changes for the entity-scoped history view.
//
// Reuses the payload-shape helpers from the Activity page instead of
// duplicating the { request, response } / legacy-flat-body interpretation.
import { getPayloadSections } from "../../pages/App/Activity/utils";
import { AuditLogEntry } from "../../services/audit/audit.types";

export interface FieldChange {
  field: string;
  from?: string;
  to: string;
}

const MAX_VALUE_LENGTH = 80;
const MAX_CREATE_FIELDS = 5;

const truncate = (value: string): string =>
  value.length > MAX_VALUE_LENGTH
    ? `${value.slice(0, MAX_VALUE_LENGTH)}…`
    : value;

/** Renders a single field's value for display: short, safe, never raw JSON. */
export const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Sí" : "No";
  if (typeof value === "object") return "(actualizado)";
  return truncate(String(value));
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
};

/**
 * Summarizes what changed in one audit entry, comparing the request it sent
 * against the response captured by the previous entry for the same entity
 * (when available).
 *
 * - DELETE entries carry no field list (the UI shows "Elemento eliminado").
 * - CREATE entries are capped to the first 5 request fields.
 * - Deep object/array values are never diffed field-by-field; they're shown
 *   as "(actualizado)" via formatFieldValue.
 */
export const summarizeChanges = (
  entry: AuditLogEntry,
  previousEntry?: AuditLogEntry
): FieldChange[] => {
  if (entry.action === "DELETE") return [];

  const { request } = getPayloadSections(entry.payload);
  const requestRecord = asRecord(request);
  if (!requestRecord) return [];

  const previousRecord = previousEntry
    ? asRecord(getPayloadSections(previousEntry.payload).response)
    : null;

  let keys = Object.keys(requestRecord);
  if (entry.action === "CREATE") {
    keys = keys.slice(0, MAX_CREATE_FIELDS);
  }

  return keys.map((field) => {
    const change: FieldChange = {
      field,
      to: formatFieldValue(requestRecord[field]),
    };
    if (previousRecord && field in previousRecord) {
      change.from = formatFieldValue(previousRecord[field]);
    }
    return change;
  });
};
